import { cn } from "@/lib/utils/index";
import { forwardRef } from "react";

interface FetchPillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "error";
  selected?: boolean;
  /** Custom hex color (e.g., "#6366f1"). When provided and selected, renders a tinted pill with this color. */
  color?: string;
  /** Size variant. "md" (default) for filter tabs, "sm" for inline labels/tags. */
  size?: "sm" | "md";
  /** Element to render. "button" (default) for interactive, "span" for display-only labels. */
  as?: "button" | "span";
  /** When true and `color` is provided, uses solid color background with white text instead of tinted. */
  solid?: boolean;
}

/** Size-specific classes */
const sizeStyles = {
  sm: "px-2 py-0.5 text-sm font-bold",
  md: "px-3 py-1.5 text-sm font-semibold",
};

/**
 * Nowadays themed pill/tag component.
 * Supports fixed variants (default, error) and custom hex colors for dynamic theming.
 * Use `as="span"` + `size="sm"` for display-only label badges.
 */
export const FetchPill = forwardRef<HTMLButtonElement, FetchPillProps>(
  (
    {
      className,
      variant = "default",
      selected,
      color,
      size = "md",
      as: Element = "button",
      solid = false,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isSelected = selected ?? false;
    const baseStyles = cn("inline-flex items-center rounded-full transition-colors", sizeStyles[size]);
    // Toggle semantics: only announce aria-pressed when the caller actually passed a `selected`
    // state. Pills used as plain action buttons (e.g. "click to copy" variable chips) aren't
    // toggles and shouldn't be announced as one just because they render as a <button>. Spans
    // (display-only badges) skip this regardless.
    const pressedAttr = Element === "button" && selected !== undefined ? { "aria-pressed": isSelected } : {};

    // Custom color mode: solid, tinted (selected), or gray (unselected)
    if (color) {
      const colorStyles = solid
        ? { backgroundColor: color, color: "#fff" }
        : isSelected
          ? { backgroundColor: `${color}30`, color }
          : undefined;
      return (
        <Element
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cn(baseStyles, isSelected ? "" : "bg-gray-100 text-gray-700 hover:bg-gray-200", className)}
          style={{ ...colorStyles, ...style }}
          {...props}
          {...(Element === "button" ? { type: "button" } : {})}
          {...pressedAttr}
        >
          {children}
        </Element>
      );
    }

    // Standard variant mode
    const variants = {
      default: isSelected
        ? "bg-indigo-100 text-indigo-700 ring-1 ring-inset ring-indigo-600 hover:bg-indigo-200"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
      error: "bg-red-100 text-red-700 hover:bg-red-200",
    };

    return (
      <Element
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(baseStyles, variants[variant], className)}
        style={style}
        {...props}
        {...(Element === "button" ? { type: "button" } : {})}
        {...pressedAttr}
      >
        {children}
      </Element>
    );
  }
);

FetchPill.displayName = "FetchPill";
